import React, { useEffect, useMemo, useState } from "react";
import { getOrgs } from "../api";
import {
  createOrgSetting,
  deleteOrgSetting,
  generateSubsidiaries,
  getOrgSettings,
  updateOrgSetting,
} from "../api/settings";

export default function Settings() {
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [orgError, setOrgError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [settingsRows, setSettingsRows] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  const [edits, setEdits] = useState({});
  const [savingByName, setSavingByName] = useState({});
  const [messageByName, setMessageByName] = useState({});
  const [deletingByName, setDeletingByName] = useState({});

  const [newSettingName, setNewSettingName] = useState("");
  const [newSettingValue, setNewSettingValue] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState("");

  const [subCompanyName, setSubCompanyName] = useState("");
  const [subGenerating, setSubGenerating] = useState(false);
  const [subError, setSubError] = useState("");
  const [subResult, setSubResult] = useState(null);
  const [subAccepting, setSubAccepting] = useState(false);

  useEffect(() => {
    let ignore = false;

    async function loadOrgs() {
      setLoadingOrgs(true);
      setOrgError("");
      try {
        const data = await getOrgs();
        if (ignore) return;
        const sorted = (Array.isArray(data) ? data : [])
          .slice()
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrgs(sorted);
        if (!selectedOrg && sorted.length) setSelectedOrg(sorted[0]);
      } catch (err) {
        if (ignore) return;
        setOrgError(`Failed to load organizations${err?.message ? ` — ${err.message}` : ""}`);
      } finally {
        if (!ignore) setLoadingOrgs(false);
      }
    }

    loadOrgs();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadSettings() {
      if (!selectedOrg?.id) {
        setSettingsRows([]);
        setEdits({});
        setSettingsError("");
        return;
      }
      setLoadingSettings(true);
      setSettingsError("");
      setMessageByName({});
      setAddError("");
      try {
        const rows = await getOrgSettings(selectedOrg.id);
        if (ignore) return;
        setSettingsRows(Array.isArray(rows) ? rows : []);
        setEdits({});
      } catch (err) {
        if (ignore) return;
        setSettingsRows([]);
        setSettingsError(`Failed to load settings${err?.message ? ` — ${err.message}` : ""}`);
      } finally {
        if (!ignore) setLoadingSettings(false);
      }
    }

    loadSettings();
    return () => {
      ignore = true;
    };
  }, [selectedOrg]);

  const filteredOrgs = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(q));
  }, [orgs, search]);

  const getDraftValue = (row) =>
    Object.prototype.hasOwnProperty.call(edits, row.setting_name)
      ? edits[row.setting_name]
      : row.setting_value;

  const isDirty = (row) => getDraftValue(row) !== row.setting_value;

  async function onAddSetting(e) {
    e.preventDefault();
    if (!selectedOrg?.id) return;

    const cleanName = newSettingName.trim();
    if (!cleanName) return;

    setAddBusy(true);
    setAddError("");
    try {
      const created = await createOrgSetting(selectedOrg.id, cleanName, newSettingValue);
      setSettingsRows((prev) =>
        [...prev, created].sort((a, b) => a.setting_name.localeCompare(b.setting_name))
      );
      setNewSettingName("");
      setNewSettingValue("");
    } catch (err) {
      setAddError(err?.message || "Failed to add setting");
    } finally {
      setAddBusy(false);
    }
  }

  async function onGenerateSubsidiaries(e) {
    e.preventDefault();
    if (!selectedOrg?.id || !subCompanyName.trim()) return;

    setSubGenerating(true);
    setSubError("");
    setSubResult(null);
    try {
      const data = await generateSubsidiaries(selectedOrg.id, subCompanyName.trim());
      setSubResult(data);
    } catch (err) {
      setSubError(err?.message || "Failed to generate subsidiaries");
    } finally {
      setSubGenerating(false);
    }
  }

  async function onAcceptSubsidiaries() {
    if (!selectedOrg?.id || !subResult?.raw_output) return;

    const value = subResult.raw_output;
    const existing = settingsRows.find((r) => r.setting_name === "corporate names");

    setSubAccepting(true);
    setSubError("");
    try {
      if (existing) {
        const updated = await updateOrgSetting(selectedOrg.id, "corporate names", value);
        setSettingsRows((prev) =>
          prev.map((r) =>
            r.setting_name === "corporate names"
              ? { ...r, setting_value: updated?.setting_value ?? value }
              : r
          )
        );
        setEdits((prev) => {
          const next = { ...prev };
          delete next["corporate names"];
          return next;
        });
      } else {
        const created = await createOrgSetting(selectedOrg.id, "corporate names", value);
        setSettingsRows((prev) =>
          [...prev, created].sort((a, b) => a.setting_name.localeCompare(b.setting_name))
        );
      }
      setSubResult(null);
      setSubCompanyName("");
      setMessageByName((prev) => ({
        ...prev,
        "corporate names": { type: "success", text: "Updated from subsidiary generator" },
      }));
    } catch (err) {
      setSubError(err?.message || "Failed to save corporate names");
    } finally {
      setSubAccepting(false);
    }
  }

  async function onSaveRow(row) {
    if (!selectedOrg?.id) return;
    const settingName = row.setting_name;
    const nextValue = getDraftValue(row);
    if (nextValue === row.setting_value) return;

    setSavingByName((prev) => ({ ...prev, [settingName]: true }));
    setMessageByName((prev) => ({ ...prev, [settingName]: null }));

    try {
      const updated = await updateOrgSetting(selectedOrg.id, settingName, nextValue);
      setSettingsRows((prev) =>
        prev.map((r) =>
          r.setting_name === settingName
            ? {
                ...r,
                setting_value: updated?.setting_value ?? null,
              }
            : r
        )
      );
      setEdits((prev) => {
        const next = { ...prev };
        delete next[settingName];
        return next;
      });
      setMessageByName((prev) => ({
        ...prev,
        [settingName]: { type: "success", text: "Saved" },
      }));
    } catch (err) {
      setMessageByName((prev) => ({
        ...prev,
        [settingName]: { type: "danger", text: err?.message || "Failed to save" },
      }));
    } finally {
      setSavingByName((prev) => ({ ...prev, [settingName]: false }));
    }
  }

  async function onDeleteRow(row) {
    if (!selectedOrg?.id) return;
    const settingName = row.setting_name;
    if (!window.confirm(`Delete setting '${settingName}'?`)) return;

    setDeletingByName((prev) => ({ ...prev, [settingName]: true }));
    setMessageByName((prev) => ({ ...prev, [settingName]: null }));

    try {
      await deleteOrgSetting(selectedOrg.id, settingName);
      setSettingsRows((prev) => prev.filter((r) => r.setting_name !== settingName));
      setEdits((prev) => {
        const next = { ...prev };
        delete next[settingName];
        return next;
      });
    } catch (err) {
      setMessageByName((prev) => ({
        ...prev,
        [settingName]: { type: "danger", text: err?.message || "Failed to delete" },
      }));
    } finally {
      setDeletingByName((prev) => ({ ...prev, [settingName]: false }));
    }
  }

  return (
    <div className="row">
      <aside className="col-12 col-md-4 col-lg-3 mb-3">
        <div className="card shadow-sm" style={{ borderRadius: "8px" }}>
          <div className="card-body">
            <h5 className="mb-3" style={{ fontWeight: 500 }}>Organizations</h5>
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Filter organizations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={loadingOrgs}
                style={{ borderRadius: "6px" }}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Select organization</label>
              {loadingOrgs ? (
                <div className="text-muted small">Loading organizations...</div>
              ) : orgError ? (
                <div className="text-danger small">{orgError}</div>
              ) : (
                <select
                  className="form-select"
                  value={selectedOrg?.id != null ? String(selectedOrg.id) : ""}
                  onChange={(e) => {
                    const org = orgs.find((o) => String(o.id) === e.target.value);
                    if (org) setSelectedOrg(org);
                  }}
                  disabled={filteredOrgs.length === 0}
                  style={{ borderRadius: "6px" }}
                >
                  {filteredOrgs.length === 0 ? (
                    <option value="">No organizations found</option>
                  ) : (
                    filteredOrgs.map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>
          </div>
        </div>
      </aside>

      <main className="col-12 col-md-8 col-lg-9">
        <div className="card shadow-sm" style={{ borderRadius: "8px" }}>
          <div className="card-body">
            <h5 className="mb-4" style={{ fontWeight: 500 }}>
              Settings {selectedOrg ? `— ${selectedOrg.name}` : ""}
            </h5>

            {!selectedOrg ? (
              <div className="text-muted">Select an organization to begin.</div>
            ) : loadingSettings ? (
              <div className="text-muted">Loading settings...</div>
            ) : settingsError ? (
              <div className="alert alert-danger py-2" role="alert">
                {settingsError}
              </div>
            ) : (
              <>
                <form className="mb-3" onSubmit={onAddSetting}>
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-4">
                      <label className="form-label mb-1">New setting_name</label>
                      <input
                        className="form-control form-control-sm"
                        value={newSettingName}
                        onChange={(e) => setNewSettingName(e.target.value)}
                        disabled={addBusy}
                        placeholder="example_key"
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1">New setting_value</label>
                      <input
                        className="form-control form-control-sm"
                        value={newSettingValue}
                        onChange={(e) => setNewSettingValue(e.target.value)}
                        disabled={addBusy}
                        placeholder="value (optional)"
                      />
                    </div>
                    <div className="col-12 col-md-2">
                      <button
                        type="submit"
                        className="btn btn-success btn-sm w-100"
                        disabled={addBusy || !newSettingName.trim()}
                      >
                        {addBusy ? "Adding..." : "Add"}
                      </button>
                    </div>
                  </div>
                  {addError && (
                    <div className="small text-danger mt-1">{addError}</div>
                  )}
                </form>
                <div
                  className="card mb-3"
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    background: "#f8f9fa",
                  }}
                >
                  <div className="card-body">
                    <h6 className="mb-3" style={{ fontWeight: 500 }}>
                      Generate Corporate Names (Subsidiaries)
                    </h6>
                    <form onSubmit={onGenerateSubsidiaries}>
                      <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-8">
                          <label className="form-label mb-1">Company Name</label>
                          <input
                            className="form-control form-control-sm"
                            value={subCompanyName}
                            onChange={(e) => setSubCompanyName(e.target.value)}
                            disabled={subGenerating || subAccepting}
                            placeholder="e.g. Alphabet Inc."
                            required
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <button
                            type="submit"
                            className="btn btn-outline-primary btn-sm w-100"
                            disabled
                            title="Corporate name generation is disabled"
                          >
                            Generate
                          </button>
                        </div>
                      </div>
                    </form>
                    {subError && (
                      <div className="small text-danger mt-2">{subError}</div>
                    )}
                    {subResult && (
                      <div className="mt-3">
                        <label className="form-label mb-1">
                          Results for <strong>{subResult.company_name}</strong>
                        </label>
                        <textarea
                          className="form-control form-control-sm mb-2"
                          value={subResult.raw_output}
                          readOnly
                          rows={Math.min(subResult.raw_output.split("\n").length + 1, 20)}
                          style={{
                            fontFamily: "Courier New, Courier, monospace",
                            whiteSpace: "pre-wrap",
                          }}
                        />
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={onAcceptSubsidiaries}
                            disabled={subAccepting}
                          >
                            {subAccepting ? "Saving..." : "Accept & Save to corporate names"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setSubResult(null)}
                            disabled={subAccepting}
                          >
                            Discard
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {settingsRows.length === 0 ? (
                  <div className="text-muted">No settings rows found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "30%" }}>setting_name</th>
                          <th style={{ width: "45%" }}>setting_value</th>
                          <th style={{ width: "25%" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settingsRows.map((row) => {
                          const draft = getDraftValue(row);
                          const isSaving = Boolean(savingByName[row.setting_name]);
                          const isDeleting = Boolean(deletingByName[row.setting_name]);
                          const dirty = isDirty(row);
                          const message = messageByName[row.setting_name];
                          const hasNewlines = String(draft ?? "").includes("\n");
                          const isReadOnly = row.setting_name === "corporate names";
                          return (
                            <tr key={row.setting_name}>
                              <td className="fw-semibold">{row.setting_name}</td>
                              <td>
                                <textarea
                                  className="form-control form-control-sm"
                                  value={draft ?? ""}
                                  onChange={(e) =>
                                    setEdits((prev) => ({
                                      ...prev,
                                      [row.setting_name]: e.target.value,
                                    }))
                                  }
                                  disabled={isSaving || isDeleting}
                                  readOnly={isReadOnly}
                                  rows={hasNewlines ? 4 : 1}
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    resize: "vertical",
                                    fontFamily: "Courier New, Courier, monospace",
                                  }}
                                />
                                {message && (
                                  <div
                                    className={`small mt-1 text-${message.type}`}
                                    role="status"
                                  >
                                    {message.text}
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={isReadOnly || !dirty || isSaving || isDeleting}
                                    onClick={() => onSaveRow(row)}
                                  >
                                    {isSaving ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    disabled={isReadOnly || !dirty || isSaving || isDeleting}
                                    onClick={() =>
                                      setEdits((prev) => {
                                        const next = { ...prev };
                                        delete next[row.setting_name];
                                        return next;
                                      })
                                    }
                                  >
                                    Reset
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    disabled={isReadOnly || isSaving || isDeleting}
                                    onClick={() => onDeleteRow(row)}
                                  >
                                    {isDeleting ? "Deleting..." : "Delete"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
